import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import snackbar from "../../../utilities/snackBar/SnackBar";
import { RulesAPI } from "../api/rules.api";
import type {
	DispatchRule,
	DispatchRuleParams,
	DispatchRuleType,
	NotificationMethod,
	ProfitCenter,
	ProfitCenterDispatchRule,
	TicketNotificationConfig,
} from "../types/rules.types";

dayjs.extend(customParseFormat);

export function useRules(initialQuery: DispatchRuleParams) {
	const history = useHistory();

	const [loading, setLoading] = useState<boolean>(true);

	const [rulesData, setRulesData] = useState<DispatchRule[]>([]);
	const [showInactiveRules, setShowInactiveRules] = useState<boolean>(false);
	const updateShowInactiveRules = (show: boolean) => {
		const params = new URLSearchParams();
		params.set("showInactive", show ? "true" : "false");
		history.replace({ search: params.toString() });

		setShowInactiveRules(show);
	};

	const [showCreateRuleModal, setShowCreateRuleModal] =
		useState<boolean>(false);
	const updateShowCreateRuleModal = (show: boolean) => {
		if (!show) {
			resetCreateRuleForm();
		}
		setShowCreateRuleModal(show);
	};

	const [profitCenters, setProfitCenters] = useState<ProfitCenter[]>([]);
	const [selectedProfitCenterId, setSelectedProfitCenterId] =
		useState<string>();
	const updateSelectedProfitCenterId = (id: string) => {
		if (Number.isNaN(Number(id))) {
			return;
		}

		setSelectedProfitCenterId(id);
	};
	const clearSelectedProfitCenterId = () => {
		setSelectedProfitCenterId(undefined);
	};

	const [selectedStartTime, setSelectedStartTime] = useState<string>();
	const updateSelectedStartTime = (time: string) => {
		if (!dayjs(time, "HH:mm", true).isValid()) {
			return;
		}

		setSelectedStartTime(time);
	};
	const clearSelectedStartTime = () => {
		setSelectedStartTime(undefined);
	};
	const [selectedEndTime, setSelectedEndTime] = useState<string>();
	const updateSelectedEndTime = (time: string) => {
		if (!dayjs(time, "HH:mm", true).isValid()) {
			return;
		}

		setSelectedEndTime(time);
	};
	const clearSelectedEndTime = () => {
		setSelectedEndTime(undefined);
	};

	const [ruleTypes, setRuleTypes] = useState<DispatchRuleType[]>([]);
	const [selectedRuleTypeId, setSelectedRuleTypeId] = useState<string>();
	const updateSelectedRuleTypeId = (id: string) => {
		if (Number.isNaN(Number(id))) {
			return;
		}

		setSelectedRuleTypeId(id);
	};
	const clearselectedRuleTypeId = () => {
		setSelectedRuleTypeId(undefined);
	};

	const [checkedDefaultNotificationType, setCheckedDefaultNotificationType] =
		useState<NotificationMethod>();
	const updateCheckedDefaultNotificationType = (type: NotificationMethod) => {
		setCheckedDefaultNotificationType(type);
	};
	const clearCheckedDefaultNotificationType = () => {
		setCheckedDefaultNotificationType(undefined);
	};

	const [checkedUpdateNotificationType, setCheckedUpdateNotificationType] =
		useState<NotificationMethod>();
	const updateCheckedUpdateNotificationType = (type: NotificationMethod) => {
		setCheckedUpdateNotificationType(type);
	};
	const clearCheckedUpdateNotificationType = () => {
		setCheckedUpdateNotificationType(undefined);
	};

	const [checkedCancelNotificationType, setCheckedCancelNotificationType] =
		useState<NotificationMethod>();
	const updateCheckedCancelNotificationType = (type: NotificationMethod) => {
		setCheckedCancelNotificationType(type);
	};
	const clearCheckedCancelNotificationType = () => {
		setCheckedCancelNotificationType(undefined);
	};

	const [checkedRecallNotificationType, setCheckedRecallNotificationType] =
		useState<NotificationMethod>();
	const updateCheckedRecallNotificationType = (type: NotificationMethod) => {
		setCheckedRecallNotificationType(type);
	};
	const clearCheckedRecallNotificationType = () => {
		setCheckedRecallNotificationType(undefined);
	};

	const showError = useCallback((message: string) => {
		snackbar({ text: message, type: "error", timeout: 4000 });
	}, []);

	const isFirst = useRef(true);
	useEffect(() => {
		if (isFirst.current) {
			isFirst.current = false;

			if (initialQuery.showInactive) {
				let show: boolean = false;

				if (initialQuery.showInactive.toLowerCase() === "true") {
					show = true;
				}

				if (initialQuery.showInactive.toLowerCase() === "false") {
					show = false;
				}

				setShowInactiveRules(show);
			}
		}
	}, [initialQuery]);

	const getRulesData = useCallback(async () => {
		try {
			const response = await RulesAPI.getDispatchRules();

			setRulesData(response);
		} catch (error) {
			if (error instanceof Error) {
				showError(error.message);
			} else {
				showError("Unexpected Error");
			}
		}
	}, [showError]);

	const createRule = async () => {
		if (!canCreateRule) {
			showError("Required fields are missing.");
			return;
		}

		if (!selectedStartTime || !selectedEndTime) {
			showError("Required fields are missing.");
			return;
		}

		const rule: ProfitCenterDispatchRule = {
			profit_center_dispatch_rule_id: 0,
			profit_center_id: Number(selectedProfitCenterId),
			rule_start_time: selectedStartTime,
			rule_end_time: selectedEndTime,
			update_ticket_notification_type: checkedUpdateNotificationType || null,
			cancel_ticket_notification_type: checkedCancelNotificationType || null,
			notification_type: checkedDefaultNotificationType || null,
			recall_ticket_notification_type: checkedRecallNotificationType || null,
			rule_type_id: Number(selectedRuleTypeId),
		};

		let success: boolean = false;
		try {
			success = await RulesAPI.createDispatchRule(rule);
		} catch (error) {
			if (error instanceof Error) {
				showError(error.message);
			} else {
				showError("Unexpected Error");
			}
		}

		if (success) {
			await getRulesData();
			updateShowCreateRuleModal(false);
		}
	};

	const _getProfitCenters = async () => {
		try {
			const response = await RulesAPI.getProfitCenters();

			setProfitCenters(response);
		} catch (error) {
			if (error instanceof Error) {
				showError(error.message);
			} else {
				showError("Unexpected Error");
			}
		}
	};

	const canCreateRule = useMemo(() => {
		return Boolean(
			selectedProfitCenterId &&
				selectedStartTime &&
				selectedEndTime &&
				checkedDefaultNotificationType &&
				checkedUpdateNotificationType &&
				checkedCancelNotificationType &&
				checkedRecallNotificationType &&
				selectedRuleTypeId,
		);
	}, [
		selectedProfitCenterId,
		selectedStartTime,
		selectedEndTime,
		checkedDefaultNotificationType,
		checkedUpdateNotificationType,
		checkedCancelNotificationType,
		checkedRecallNotificationType,
		selectedRuleTypeId,
	]);

	const resetCreateRuleForm = useCallback(() => {
		setSelectedProfitCenterId(undefined);
		setSelectedStartTime(undefined);
		setSelectedEndTime(undefined);
		setSelectedRuleTypeId(undefined);
		setCheckedDefaultNotificationType(undefined);
		setCheckedUpdateNotificationType(undefined);
		setCheckedCancelNotificationType(undefined);
		setCheckedRecallNotificationType(undefined);
	}, []);

	const TicketNotificationConfigs: TicketNotificationConfig[] = [
		{
			label: "Default",
			value: checkedDefaultNotificationType,
			UpdateValue: (method: NotificationMethod) =>
				updateCheckedDefaultNotificationType(method),
		},
		{
			label: "Update",
			value: checkedUpdateNotificationType,
			UpdateValue: (method: NotificationMethod) =>
				updateCheckedUpdateNotificationType(method),
		},
		{
			label: "Cancel",
			value: checkedCancelNotificationType,
			UpdateValue: (method: NotificationMethod) =>
				updateCheckedCancelNotificationType(method),
		},
		{
			label: "Recall",
			value: checkedRecallNotificationType,
			UpdateValue: (method: NotificationMethod) =>
				updateCheckedRecallNotificationType(method),
		},
	];

	const hasRun = useRef(false);
	useEffect(() => {
		if (hasRun.current) return;
		hasRun.current = true;

		const getProfitCenters = async () => {
			try {
				const response = await RulesAPI.getProfitCenters();
				setProfitCenters(response);
			} catch (error) {
				if (error instanceof Error) {
					showError(error.message);
				} else {
					showError("Unexpected Error");
				}
			}
		};

		const getRuleTypes = async () => {
			try {
				const response = await RulesAPI.getRuleTypes();
				setRuleTypes(response);
			} catch (error) {
				if (error instanceof Error) {
					showError(error.message);
				} else {
					showError("Unexpected Error");
				}
			}
		};

		const load = async () => {
			setLoading(true);

			try {
				// Initialization logic
				await Promise.all([getRulesData(), getProfitCenters(), getRuleTypes()]);
			} finally {
				setLoading(false);
			}
		};

		load();
	}, [getRulesData, showError]);

	const [showEditRuleModal, setShowEditRuleModal] = useState<boolean>(false);
	const updateShowEditRuleModal = (show: boolean) => {
		if (!show) {
			resetEditRuleForm();
		}
		setShowEditRuleModal(show);
	};
	const handleRuleRowClick = (rule: DispatchRule) => {
		if (rule.deleted_date != null) {
			showError("Cannot edit an inactive rule.");
			return;
		}
		setEditProfitCenterDispatchRuleId(rule.profit_center_dispatch_rule_id);

		updateEditRuleTypeId(rule.rule_type?.rule_type_id?.toString() || "");
		updateEditProfitCenterId(rule.profit_center_id.toString());
		updateEditStartTime(
			dayjs(rule.rule_start_time, "HH:mm:ss").format("HH:mm"),
		);
		updateEditEndTime(dayjs(rule.rule_end_time, "HH:mm:ss").format("HH:mm"));

		updateCheckedEditDefaultNotificationType(
			Number(rule.notification_type) as NotificationMethod,
		);
		updateCheckedEditUpdateNotificationType(
			Number(rule.update_ticket_notification_type) as NotificationMethod,
		);
		updateCheckedEditCancelNotificationType(
			Number(rule.cancel_ticket_notification_type) as NotificationMethod,
		);
		updateCheckedEditRecallNotificationType(
			Number(rule.recall_ticket_notification_type) as NotificationMethod,
		);

		updateShowEditRuleModal(true);
	};

	const [editProfitCenterDispatchRuleId, setEditProfitCenterDispatchRuleId] =
		useState<number>();

	const [editRuleTypeId, setEditRuleTypeId] = useState<string>();
	const updateEditRuleTypeId = (id: string) => {
		if (Number.isNaN(Number(id))) {
			return;
		}

		setEditRuleTypeId(id);
	};

	const [editProfitCenterId, setEditProfitCenterId] = useState<string>();
	const updateEditProfitCenterId = (id: string) => {
		if (Number.isNaN(Number(id))) {
			return;
		}

		setEditProfitCenterId(id);
	};

	const [editStartTime, setEditStartTime] = useState<string>();
	const updateEditStartTime = (time: string) => {
		if (!dayjs(time, "HH:mm", true).isValid()) {
			return;
		}

		setEditStartTime(time);
	};

	const [editEndTime, setEditEndTime] = useState<string>();
	const updateEditEndTime = (time: string) => {
		if (!dayjs(time, "HH:mm", true).isValid()) {
			return;
		}

		setEditEndTime(time);
	};

	const [
		checkedEditDefaultNotificationType,
		setCheckedEditDefaultNotificationType,
	] = useState<NotificationMethod>();
	const updateCheckedEditDefaultNotificationType = (
		type: NotificationMethod,
	) => {
		setCheckedEditDefaultNotificationType(type);
	};

	const [
		checkedEditUpdateNotificationType,
		setCheckedEditUpdateNotificationType,
	] = useState<NotificationMethod>();
	const updateCheckedEditUpdateNotificationType = (
		type: NotificationMethod,
	) => {
		setCheckedEditUpdateNotificationType(type);
	};

	const [
		checkedEditCancelNotificationType,
		setCheckedEditCancelNotificationType,
	] = useState<NotificationMethod>();
	const updateCheckedEditCancelNotificationType = (
		type: NotificationMethod,
	) => {
		setCheckedEditCancelNotificationType(type);
	};

	const [
		checkedEditRecallNotificationType,
		setCheckedEditRecallNotificationType,
	] = useState<NotificationMethod>();
	const updateCheckedEditRecallNotificationType = (
		type: NotificationMethod,
	) => {
		setCheckedEditRecallNotificationType(type);
	};

	const EditTicketNotificationConfigs: TicketNotificationConfig[] = [
		{
			label: "Default",
			value: checkedEditDefaultNotificationType,
			UpdateValue: (method: NotificationMethod) =>
				updateCheckedEditDefaultNotificationType(method),
		},
		{
			label: "Update",
			value: checkedEditUpdateNotificationType,
			UpdateValue: (method: NotificationMethod) =>
				updateCheckedEditUpdateNotificationType(method),
		},
		{
			label: "Cancel",
			value: checkedEditCancelNotificationType,
			UpdateValue: (method: NotificationMethod) =>
				updateCheckedEditCancelNotificationType(method),
		},
		{
			label: "Recall",
			value: checkedEditRecallNotificationType,
			UpdateValue: (method: NotificationMethod) =>
				updateCheckedEditRecallNotificationType(method),
		},
	];

	const canEditRule = useMemo(() => {
		const rule = rulesData.find(
			(x) =>
				x.profit_center_dispatch_rule_id === editProfitCenterDispatchRuleId,
		);
		if (!rule) {
			return false;
		}

		const result = Boolean(
			rule.profit_center_id === Number(editProfitCenterId) &&
				dayjs(rule.rule_start_time, "HH:mm").format("HH:mm") ===
					dayjs(editStartTime, "HH:mm").format("HH:mm") &&
				dayjs(rule.rule_end_time, "HH:mm").format("HH:mm") ===
					dayjs(editEndTime, "HH:mm").format("HH:mm") &&
				rule.notification_type === Number(checkedEditDefaultNotificationType) &&
				rule.update_ticket_notification_type ===
					Number(checkedEditUpdateNotificationType) &&
				rule.cancel_ticket_notification_type ===
					Number(checkedEditCancelNotificationType) &&
				rule.recall_ticket_notification_type ===
					Number(checkedEditRecallNotificationType) &&
				rule.rule_type?.rule_type_id === Number(editRuleTypeId),
		);

		return !result;
	}, [
		editProfitCenterId,
		editStartTime,
		editEndTime,
		checkedEditDefaultNotificationType,
		checkedEditUpdateNotificationType,
		checkedEditCancelNotificationType,
		checkedEditRecallNotificationType,
		editRuleTypeId,
		rulesData.find,
		editProfitCenterDispatchRuleId,
	]);

	const resetEditRuleForm = useCallback(() => {
		setEditProfitCenterId(undefined);
		setEditStartTime(undefined);
		setEditEndTime(undefined);
		setEditRuleTypeId(undefined);
		setCheckedEditDefaultNotificationType(undefined);
		setCheckedEditUpdateNotificationType(undefined);
		setCheckedEditCancelNotificationType(undefined);
		setCheckedEditRecallNotificationType(undefined);
		setEditProfitCenterDispatchRuleId(undefined);
	}, []);

	const editRule = async () => {
		if (!canEditRule) {
			showError("No changes were made.");
			return;
		}

		if (
			editProfitCenterDispatchRuleId == null ||
			editProfitCenterId == null ||
			editStartTime == null ||
			editEndTime == null ||
			editRuleTypeId == null ||
			checkedEditUpdateNotificationType == null ||
			checkedEditCancelNotificationType == null ||
			checkedEditDefaultNotificationType == null ||
			checkedEditRecallNotificationType == null
		) {
			showError("Missing required fields");
			return;
		}

		const rule: ProfitCenterDispatchRule = {
			profit_center_dispatch_rule_id: editProfitCenterDispatchRuleId,
			profit_center_id: Number(editProfitCenterId),
			rule_start_time: editStartTime,
			rule_end_time: editEndTime,
			update_ticket_notification_type: checkedEditUpdateNotificationType,
			cancel_ticket_notification_type: checkedEditCancelNotificationType,
			notification_type: checkedEditDefaultNotificationType,
			recall_ticket_notification_type: checkedEditRecallNotificationType,
			rule_type_id: Number(editRuleTypeId),
		};

		let success: boolean = false;
		try {
			success = await RulesAPI.editDispatchRule(rule);
		} catch (error) {
			if (error instanceof Error) {
				showError(error.message);
			} else {
				showError("Unexpected Error");
			}
		}

		if (success) {
			await getRulesData();
			updateShowEditRuleModal(false);
		}
	};

	const deleteRule = async () => {
		if (!editProfitCenterDispatchRuleId) {
			showError("No rule was selected.");
			return;
		}

		let success: boolean = false;
		try {
			success = await RulesAPI.deleteDispatchRule(
				editProfitCenterDispatchRuleId,
			);
		} catch (error) {
			if (error instanceof Error) {
				showError(error.message);
			} else {
				showError("Unexpected Error");
			}
		}

		if (success) {
			await getRulesData();
			updateShowEditRuleModal(false);
		}
	};

	const canDeleteRule = useMemo(() => {
		const rule = rulesData.find(
			(x) =>
				x.profit_center_dispatch_rule_id === editProfitCenterDispatchRuleId,
		);
		if (!rule) {
			return false;
		}

		return !rule.deleted_date;
	}, [editProfitCenterDispatchRuleId, rulesData.find]);

	return {
		loading,
		rulesData,
		getRulesData,
		showInactiveRules,
		updateShowInactiveRules,
		showCreateRuleModal,
		updateShowCreateRuleModal,
		profitCenters,
		selectedProfitCenterId,
		updateSelectedProfitCenterId,
		clearSelectedProfitCenterId,
		checkedDefaultNotificationType,
		updateCheckedDefaultNotificationType,
		clearCheckedDefaultNotificationType,
		checkedUpdateNotificationType,
		updateCheckedUpdateNotificationType,
		clearCheckedUpdateNotificationType,
		checkedCancelNotificationType,
		updateCheckedCancelNotificationType,
		clearCheckedCancelNotificationType,
		checkedRecallNotificationType,
		updateCheckedRecallNotificationType,
		clearCheckedRecallNotificationType,
		TicketNotificationConfigs,
		ruleTypes,
		selectedRuleTypeId,
		updateSelectedRuleTypeId,
		createRule,
		selectedStartTime,
		selectedEndTime,
		updateSelectedStartTime,
		updateSelectedEndTime,
		clearselectedRuleTypeId,
		clearSelectedStartTime,
		clearSelectedEndTime,
		showError,
		canCreateRule,
		handleRuleRowClick,
		showEditRuleModal,
		updateShowEditRuleModal,
		EditTicketNotificationConfigs,
		editRuleTypeId,
		updateEditRuleTypeId,
		editProfitCenterId,
		updateEditProfitCenterId,
		editStartTime,
		updateEditStartTime,
		editEndTime,
		updateEditEndTime,
		canEditRule,
		editRule,
		canDeleteRule,
		deleteRule,
	};
}
